import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import { authApi } from '../../api/endpoints';
import { useAutofillSync } from '../../hooks/useAutofillSync';
import { Field, TextInput } from '../../components/Field';
import Icon from '../../components/Icon';

export default function ForgotPassword() {
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm({ defaultValues: { email: '' } });

  // Browser autofill bypasses the events react-hook-form listens for.
  const { formRef, readDom } = useAutofillSync({ setValue, fields: ['email'] });

  const onSubmit = async (values) => {
    setError(null);
    try {
      await authApi.forgotPassword({ email: readDom('email', values.email) });
      // The API deliberately reports success either way, so the confirmation
      // screen never reveals whether the account exists.
      setSent(true);
    } catch (err) {
      setError(err.message);
    }
  };

  if (sent) {
    return (
      <div className="auth-shell">
        <div className="auth-card" style={{ textAlign: 'center' }}>
          <div
            className="auth-logo"
            style={{ margin: '0 auto 18px', background: 'var(--success-bg)', color: 'var(--success-fg)', boxShadow: 'none' }}
          >
            <Icon name="mark_email_read" style={{ fontSize: 24 }} />
          </div>
          <h1 className="auth-title">Check your email</h1>
          <p className="auth-sub">
            If an account exists for <strong>{getValues('email')}</strong>, we have sent a link to reset the
            password. It expires in 30 minutes.
          </p>
          <Link to="/login" className="btn-ghost" style={{ width: '100%', justifyContent: 'center' }}>
            Back to sign in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-shell">
      <div className="auth-card">
        <img className="auth-logo-img" src="/photos/logo.png" alt="My Desi Guru" />
        <h1 className="auth-title">Forgot password</h1>
        <p className="auth-sub">Enter your email and we will send you a reset link.</p>

        {error && (
          <div
            role="alert"
            style={{
              background: 'var(--danger-bg)',
              color: 'var(--danger-fg)',
              padding: '11px 14px',
              borderRadius: 'var(--radius)',
              fontSize: 13,
              fontWeight: 600,
              marginBottom: 18,
            }}
          >
            {error}
          </div>
        )}

        <form ref={formRef} onSubmit={handleSubmit(onSubmit)} noValidate>
          <Field label="Email" error={errors.email?.message} required>
            <TextInput
              type="email"
              autoComplete="username"
              placeholder="you@mydesiguru.com"
              autoFocus
              error={errors.email}
              {...register('email', {
                validate: (v) => {
                  const value = v || readDom('email', '');
                  if (!value) return 'Enter your email address';
                  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value)) return 'Enter a valid email address';
                  return true;
                },
              })}
            />
          </Field>

          <button type="submit" className="btn-brand" style={{ width: '100%', justifyContent: 'center' }} disabled={isSubmitting}>
            {isSubmitting && <span className="spinner" />}
            Send reset link
          </button>

          <div style={{ textAlign: 'center', marginTop: 18 }}>
            <Link to="/login" style={{ fontSize: 12, fontWeight: 600 }}>
              Back to sign in
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
